import { InstallationDetailsMappingService } from './../../../services/installation-details-mapping.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { InstallationService } from 'src/app/+installations/services/installation.service';

import {
  DeviceStatusType,
  EssSystemType,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  IsStringValueProperty,
  NumberValue,
  PowerLimitationTooltip,
  PropertiesValue,
  SummaryConsumptionProperties,
} from '@vi-iot/client';

import {
  DeviceTypes,
  DevicesStore,
  FeatureService,
  InstallationDetailsStore,
  InstallationPanelOrder,
  isReady,
  RichDevice,
  defaultPanelsOrder,
  InstallationFeaturesStore,
  isLeadCascadeV2Device,
} from '@store';

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Chart } from '../../../../features/chart-bar/models/chart.model';
import { DragDropLayoutService } from '../../drag-drop-layout/drag-drop-layout.service';
import { DeviceFeatureTilesBase } from '../device-feature-tiles-base';
import { PillType } from './../../../../shared/components/pill/pill.model';
import { CustomTranslationsService } from 'src/app/operational-data-common/translations';
import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import { ElectricHeaterService } from '../../electric-heater/electric-heater.service';

@Component({
  selector: 'app-v2-device-feature-tiles',
  templateUrl: './v2-device-feature-tiles.component.html',
  styleUrls: ['./v2-device-feature-tiles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2DeviceFeatureTilesComponent extends DeviceFeatureTilesBase implements OnInit {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly essSystemType = EssSystemType;
  readonly pillType = PillType;
  readonly DeviceStatusType = DeviceStatusType;

  @Input() device: RichDevice;
  @Input() cascadeIndex: number;

  isLeadCascadeV2Device: boolean;
  powerProductionChartData$: Observable<Chart>;
  solarPowerProductionChartData$: Observable<Chart>;
  gasConsumptionChartData$: Observable<Chart>;
  electricityConsumptionChartData$: Observable<Chart>;
  powerCumulativeProducedFeature$: Observable<Feature<IsNumberValueProperty, never>>;
  heatProductionChartData$: Observable<Chart>;
  gasConsumptionSummaryHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  gasConsumptionSummaryDhw$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionSummaryHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionSummaryDhw$: Observable<Feature<SummaryConsumptionProperties, never>>;
  autarkySummary$: Observable<Feature<SummaryConsumptionProperties, never>>;
  gasConsumptionHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  powerConsumptionHeating$: Observable<Feature<SummaryConsumptionProperties, never>>;
  isEssBatteryOrHybrid$: Observable<boolean>;
  heatingDeviceType = DeviceTypes.Heating;
  panels: InstallationPanelOrder[];
  isDragDropDisabled: boolean;
  memberId$: Observable<Feature<IsNumberValueProperty, never>>;
  isVicareThermostatMode$: Observable<boolean>;
  bufferTank$: Observable<Feature<IsStringValueProperty, never>>;
  powerLimitationLabel$: Observable<PowerLimitationTooltip[]>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public devicesStore: DevicesStore,
    public featureService: FeatureService,
    public installationService: InstallationService,
    public installationFeaturesStore: InstallationFeaturesStore,
    public customTranslationsService: CustomTranslationsService,
    public electricHeaterService: ElectricHeaterService,
    private cdRef: ChangeDetectorRef,
    private dragDropLayoutService: DragDropLayoutService,
    private installationDetailsMappingService: InstallationDetailsMappingService
  ) {
    super(
      installationDetailsStore,
      devicesStore,
      featureService,
      installationService,
      installationFeaturesStore
    );
  }

  ngOnInit(): void {
    this.powerLimitationLabel$ = this.installationDetailsMappingService.powerLimitationLabel(
      this.device.id
    );
    this.isLeadCascadeV2Device = isLeadCascadeV2Device(this.device);

    this.dragDropLayoutService.isDragDropDisabled
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isDragDropDisabled) => {
        this.isDragDropDisabled = isDragDropDisabled;
        this.cdRef.markForCheck();
      });
    super.initializeDeviceFeatures(this.device?.selectId, this.device?.id);

    this.powerProductionChartData$ = this.featureService.selectPowerProductionChartData(
      this.device.id
    );
    this.solarPowerProductionChartData$ = this.featureService.selectSolarPowerProductionChartData(
      this.device.id
    );
    this.heatProductionChartData$ = this.featureService.selectHeatProductionChartData(
      this.device.id
    );
    this.gasConsumptionChartData$ = this.featureService.selectGasConsumptionChartData(
      this.device.id
    );
    this.electricityConsumptionChartData$ = this.featureService.selectPowerConsumptionChartData(
      this.device.id
    );
    this.powerCumulativeProducedFeature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerCumulativeProduced
    );
    this.gasConsumptionSummaryHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionSummaryHeating
    );
    this.gasConsumptionSummaryDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionSummaryDhw
    );
    this.powerConsumptionSummaryHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionSummaryHeating
    );
    this.powerConsumptionSummaryDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionSummaryDhw
    );
    this.autarkySummary$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingPowerProductionDemandCoverageRaw
    );
    this.gasConsumptionHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.gasConsumptionHeating
    );
    this.powerConsumptionHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsumptionHeating
    );
    this.bufferTank$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.bufferCylinderOperatingModes,
      this.device.id,
      this.device.gatewaySerial
    );

    this.isVicareThermostatMode$ = this.installationFeaturesStore.isVicareThermostatMode();
    this.installationDetailsStore
      .getPanelsOrderForDevice(this.device.gatewaySerial + '-' + this.device.id)
      .subscribe((panelsOrder: InstallationPanelOrder[]) => {
        if (panelsOrder?.length > 0) {
          this.panels = panelsOrder;
        } else {
          this.panels = defaultPanelsOrder;
        }

        this.cdRef.markForCheck();
      });
    this.memberId$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      PropertiesValue<NumberValue>,
      never
    >(FeatureNames.cascadeMemberId, this.device.id);
    this.isEssBatteryOrHybrid$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.essSystemType, this.device.id)
      .pipe(
        filter(
          (feature: Feature<IsStringValueProperty, never>) =>
            isReady(feature) && !!feature?.properties?.value?.value
        ),
        map((feature) => {
          const systemType = feature.properties.value.value;

          return (
            systemType === this.essSystemType.battery || systemType === this.essSystemType.hybrid
          );
        })
      );
  }

  drop(event: CdkDragDrop<string>): void {
    this.dragDropLayoutService.updatePanelOrderListAfterDrop(
      event.previousContainer.data,
      event.container.data,
      this.panels,
      this.device.gatewaySerial + '-' + this.device.id
    );
  }

  trackBy(_index: number, panel: InstallationPanelOrder): string {
    return panel.panel;
  }
}
